import { X, MapPin, Calendar, Globe, Ticket, User } from 'lucide-react';

const ConcertDetailsOverlay = ({ concert, onClose, dispatch }) => {
  if (!concert) return null;

  return (
    <div className="fixed top-[145px] bottom-0 left-0 right-0 z-[1000] flex justify-center items-stretch pointer-events-auto p-4 md:p-6">
      <div className="absolute inset-0 bg-transparent" onClick={onClose} />
      <div className="flex flex-col md:flex-row items-stretch gap-4 relative z-10 pointer-events-auto w-full md:w-auto">
        <div className="w-full md:w-[450px] max-h-[60vh] md:max-h-[70vh] bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-zinc-200/60 overflow-hidden pointer-events-auto animate-in fade-in slide-in-from-top-4 duration-300 flex flex-col">
          <div className="p-5 flex flex-col gap-3 overflow-y-auto custom-scrollbar relative">
            <div className="flex flex-col gap-1 border-b border-zinc-100 pb-3 relative">
              <button onClick={onClose} className="absolute top-0 right-0 p-1.5 text-zinc-400 hover:text-zinc-700 bg-zinc-50 hover:bg-zinc-100 rounded-full transition-colors z-20">
                <X size={16} />
              </button>
              <span className="text-[9px] font-black uppercase tracking-widest text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full w-fit flex items-center gap-1">
                <Ticket size={10} /> Concert
              </span>
              <h3 className="text-xl font-black text-zinc-900 leading-tight pr-8">
                {concert.title || concert.artist}
                {concert._distance && <span className="text-zinc-500 font-medium ml-2 text-base whitespace-nowrap">({concert._distance} mi)</span>}
              </h3>
              {concert.title && concert.artist && concert.title !== concert.artist && (
                <div className="flex items-center gap-1.5 text-sm font-bold text-zinc-500 mt-1">
                  <User size={14} className="text-zinc-400" />
                  {concert.artist}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 mt-1">
              {concert.venue && (
                <div className="flex items-start gap-2 text-zinc-600">
                  <MapPin size={14} className="mt-0.5 shrink-0 text-zinc-400" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-bold leading-tight">{concert.venue}</span>
                    {concert.city && <span className="text-xs font-medium text-zinc-500">{concert.city}</span>}
                  </div>
                </div>
              )}
              {concert.startDate && (
                <div className="flex items-start gap-2 text-zinc-600">
                  <Calendar size={14} className="mt-0.5 shrink-0 text-zinc-400" />
                  <span className="text-sm font-medium leading-tight">
                    {new Date(concert.startDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
              )}
              {concert.songkickUrl && (
                <div className="flex items-start gap-2 text-zinc-600">
                  <Globe size={14} className="mt-0.5 shrink-0 text-zinc-400" />
                  <a href={concert.songkickUrl} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-600 hover:underline truncate">
                    View on Songkick
                  </a>
                </div>
              )}
            </div>
            
            <div className="mt-4 border-t border-zinc-100 pt-4 flex gap-2">
              <button 
                onClick={async () => {
                   if (!dispatch) return;
                   const { collection, serverTimestamp } = await import('firebase/firestore');
                   const { db, auth } = await import('../firebase');
                   const { appId: APP_ID_CONSTANT } = await import('../constants');
                   const { safeAddDoc } = await import('../utils');
                   const title = concert.title || concert.artist;
                   const note = `Artist: ${concert.artist || 'Unknown'}\nVenue: ${concert.venue || 'Unknown'}\nLink: ${concert.songkickUrl || ''}`;
                   const docRef = await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events'), {
                     type: 'tentative',
                     title: title,
                     startDate: concert.startDate,
                     endDate: concert.startDate,
                     isAllDay: true,
                     createdAt: serverTimestamp(),
                     createdBy: auth.currentUser?.uid || 'test-uid-123',
                     city: concert.city || '',
                     placeId: null,
                     lat: concert.lat || null,
                     lng: concert.lng || null,
                     note: note
                   });
                   
                   dispatch({ 
                     type: 'OPEN_EDIT_EVENT', 
                     payload: { 
                       id: docRef.id, 
                       type: 'tentative', 
                       title: title,
                       startDate: concert.startDate,
                       endDate: concert.startDate,
                       venueName: concert.venue || '',
                       address: concert.venue || '',
                       addressLat: concert.lat || null,
                       addressLng: concert.lng || null,
                       city: concert.city || '',
                       note: note
                     } 
                   });
                   onClose();
                }}
                className="flex-1 bg-zinc-900 text-white font-bold py-2.5 rounded-lg text-xs hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2"
              >
                Add to Tentative Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConcertDetailsOverlay;
